/**
 * Notifications context — owns the unread count behind the Inbox nav badge
 * (docs/FRONTEND-PLAN.md §2.1).
 *
 * The count comes from `GET /notifications/unread`, whose payload carries a
 * true total (`unreadCount`) alongside the page of items. We ask for
 * `limit: 1` and ignore the items: the badge needs the number, not the feed —
 * counting `items.length` would silently cap at the page size.
 *
 * Refreshed on mount, every 60s, whenever the tab becomes visible again, and
 * on demand via `refresh()` after a mutation (mark read, dismiss, read-all).
 * Failures are swallowed: a badge that keeps its last known value is better
 * than an error banner for a background poll.
 *
 * Mounted inside the authenticated tree (App wraps <Layout> with it) so it
 * never polls on the login/register screens.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import * as notificationsApi from '../api/notifications';
import { useAuth } from './auth';

const POLL_INTERVAL_MS = 60_000;

const NotificationsContext = createContext(null);

export function NotificationsProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  const liveRef = useRef(false);
  useEffect(() => {
    liveRef.current = isAuthenticated;
  }, [isAuthenticated]);
  useEffect(
    () => () => {
      liveRef.current = false;
    },
    []
  );

  const refresh = useCallback(async () => {
    if (!liveRef.current) return;
    try {
      const data = await notificationsApi.listUnread({ limit: 1 });
      if (!liveRef.current) return;
      if (data && typeof data.unreadCount === 'number') setUnreadCount(data.unreadCount);
    } catch {
      // Silent by design — keep the last known count.
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      return undefined;
    }
    refresh();

    const timer = setInterval(refresh, POLL_INTERVAL_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isAuthenticated, refresh]);

  const value = useMemo(() => ({ unreadCount, refresh }), [unreadCount, refresh]);

  return (
    <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotifications must be used inside <NotificationsProvider>');
  return ctx;
}
